import { useNavigate } from 'react-router-dom'
import useModals from 'src/Hooks/useModals'
import {
  getDefaultEebus,
  getDefaultEmc,
  getDefaultHeatpumpIO,
  getDefaultHtc320hIoInterface,
  getDefaultMeter,
  getDefaultModbusTcp,
  getDefaultMyPvIoInterface,
  getDefaultStorage,
  getDefaultStorageIoInterface,
} from '../../constants'
import { EebusChargingStation, getDefaultStorageSystem, Htc320hIo, ModbusTcpChargingStation, StorageSystem } from '../../installation/forms'
import { useInstallationForm } from '../../installation/hooks'
import { evseDevicesConfig, EvseReference, getEvseCategory } from '../../parsers'
import { logError } from '../../utils'
import { installationPaths } from '../constants'
import { AddDeviceTab } from '../overview'
import { Meter, MeterDeviceType } from '../powermeter'

export const useAddDeviceDialog = (): ((tab: AddDeviceTab | null) => void) => {
  const { openAddDeviceDialog } = useModals()
  const navigate = useNavigate()

  /* setTimeout is used so the navigation happens as a macrotask and the state has time to update. */
  const navigateTo = (path: string): void => {
    setTimeout(() => navigate(`../${path}`), 0)
  }

  const { getValues, setValue } = useInstallationForm()

  const addEmc = (name: string): void => {
    setValue('emcDevice', getDefaultEmc(name), { shouldDirty: true })
    navigateTo(installationPaths.General)
  }

  const addStorage = (name: string, previousStorageSystem: StorageSystem | null): void => {
    setValue('storageDevices', [...getValues('storageDevices'), getDefaultStorage(name)], { shouldDirty: true })

    if (previousStorageSystem) {
      setValue('storageSystem', previousStorageSystem, { shouldDirty: true })
    } else {
      const storageSystem = getValues('storageSystem')
      const defaultStorageSystem = getDefaultStorageSystem()

      setValue(
        'storageSystem',
        {
          ...defaultStorageSystem,
          ...storageSystem,
          storageSpecificSettings: [...storageSystem.storageSpecificSettings, ...defaultStorageSystem.storageSpecificSettings],
        },
        { shouldDirty: true }
      )
    }

    navigateTo(installationPaths.Storage)
  }

  const addWallbox = (name: string, reference: EvseReference): void => {
    const evseConfig = evseDevicesConfig[reference]
    const deviceCategory = getEvseCategory(reference)
    if (deviceCategory === 'ModbusTCP') {
      const modbusTcpArray = getValues('chargingStations.modbusTcp')
      const isEmcSinglePhase = getValues('generalSettings.gridSettings.numberOfPhases') === 'single-phase'

      const newDevice = getDefaultModbusTcp(name, isEmcSinglePhase, 'Hager', reference as ModbusTcpChargingStation['reference'])

      setValue('chargingStations.modbusTcp', [...modbusTcpArray, newDevice], {
        shouldDirty: true,
      })
      setValue(`chargingStationsExpandedState.modbusTcp.${newDevice.id}`, true)
    } else if (deviceCategory === 'EEBus') {
      const eebusArray = getValues('chargingStations.eebus')

      const newDevice = getDefaultEebus(name, evseConfig.brand, reference as EebusChargingStation['reference'])
      setValue('chargingStations.eebus', [...eebusArray, newDevice], {
        shouldDirty: true,
      })
      setValue(`chargingStationsExpandedState.eebus.${newDevice.id}`, true)
    }
    navigateTo(installationPaths.Evse)
  }

  const addHeatpump = (name: string): void => {
    setValue('heatpump', getDefaultHeatpumpIO(name), { shouldDirty: true })
    navigateTo(installationPaths.Hvac)
  }

  const addMeter = (name: string, deviceType: MeterDeviceType, peakPower: number): void => {
    const htc320hIo = getValues('ioInterfaces.htc320h')
    const metersArray = getValues('meters')

    const address = getAvailableMeterAddress(metersArray, htc320hIo)

    if (address === null) {
      logError('No available address was found for meter.')
      return
    }

    setValue('meters', [...metersArray, getDefaultMeter(name, deviceType, peakPower, address)], {
      shouldDirty: true,
    })

    navigateTo(installationPaths.Meters)
  }

  /**
   * Creates an I/O interface for a storage. The 'deviceType'  the id of the storageDeviceLink, so the I/O interface can be linked to the correct storage.
   * @param name Name of the I/O interface, can be freely chosen by the user.
   * @param deviceType Either the deviceTypes 'htc320h' and 'mypv' or the id of the storageDeviceLink for storage I/Os.
   */
  const addIoInterface = (name: string, deviceType: string): void => {
    if (deviceType === 'htc320h') {
      const metersArray = getValues('meters')
      const address = getAvailableHtc320hIoAddress(metersArray)

      if (address === null) {
        logError('No available address was found for htc320h I/O.')
        return
      }
      setValue('ioInterfaces.htc320h', getDefaultHtc320hIoInterface(name, address, getValues()), {
        shouldDirty: true,
      })
    } else if (deviceType === 'mypv') {
      setValue('ioInterfaces.mypv', getDefaultMyPvIoInterface(name), { shouldDirty: true })
    } else if (deviceType) {
      // For storages the 'deviceType' is the id of the storageDeviceLink → used to identify the name
      setValue('ioInterfaces.storage', getDefaultStorageIoInterface(name, deviceType), { shouldDirty: true })
    }

    navigateTo(installationPaths.Io)
  }

  return (tab: AddDeviceTab | null): void =>
    openAddDeviceDialog({
      installation: getValues(),
      initialTab: tab,
      addEmc,
      addStorage,
      addWallbox,
      addHeatpump,
      addMeter,
      addIoInterface,
    })
}

const getAvailableMeterAddress = (meters: Meter[], htc320hIo: Htc320hIo | null): string | null => {
  const meterAddresses = meters.map(meter => meter.pairingId)
  for (let addressIndex = 1; addressIndex <= 250; addressIndex++) {
    const address = addressIndex.toString()
    if (!meterAddresses.includes(address) && htc320hIo?.pairingId !== address) {
      return address
    }
  }

  return null
}

const getAvailableHtc320hIoAddress = (meters: Meter[]): string | null => {
  const meterAddresses = meters.map(meter => meter.pairingId)

  // First check addresses starting from 5.
  for (let addressIndex = 5; addressIndex < 100; addressIndex++) {
    const address = addressIndex.toString()
    if (!meterAddresses.includes(address)) return address
  }

  // This code is realistically unreachable as the addresses will never run out.
  // Still it is here for the sake of completion.
  // Check addresses 1 to 5.
  for (let addressIndex = 1; addressIndex < 5; addressIndex++) {
    const address = addressIndex.toString()
    if (!meterAddresses.includes(address)) return address
  }

  return null
}
